import {useEffect, useRef, useState} from 'react'
import {Link as RouterLink} from 'react-router-dom'
import {Trans, useTranslation} from 'react-i18next'
import ContactUsButton from '../components/ContactUsButton'
import {useConsent} from '../../common/cookieConsent/store'
import {writeConsent} from '../../common/cookieConsent/sync'
import {trackEvent} from '../../common/analytics/trackEvent'
import './CookiesPage.css'

interface TocEntry {
  readonly id: string
  readonly key: string
}

const TOC: readonly TocEntry[] = [
  {id: 'prefs', key: 'prefs'},
  {id: 'intro', key: 'intro'},
  {id: 'what', key: 'what'},
  {id: 'not', key: 'not'},
  {id: 'control', key: 'control'},
  {id: 'changes', key: 'changes'},
]

const PROSE_COMPONENTS = {
  contact: <RouterLink to="/contact" />,
  privacy: <RouterLink to="/privacy" />,
  terms: <RouterLink to="/terms" />,
  gaOptOut: <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noreferrer" />,
  strong: <strong />,
  em: <em />,
  code: <code />,
}

const pad2 = (n: number) => String(n).padStart(2, '0')
const SAVED_FLASH_MS = 2200

const CookiePrefsPanel = () => {
  const {t} = useTranslation()
  const consent = useConsent()
  const [saved, setSaved] = useState(false)
  const flashTimer = useRef<number | null>(null)

  const analyticsOn = consent?.analytics === true

  useEffect(
    () => () => {
      if (flashTimer.current) window.clearTimeout(flashTimer.current)
    },
    [],
  )

  const flashSaved = () => {
    setSaved(true)
    if (flashTimer.current) window.clearTimeout(flashTimer.current)
    flashTimer.current = window.setTimeout(() => setSaved(false), SAVED_FLASH_MS)
  }

  const handleToggleAnalytics = (next: boolean) => {
    writeConsent(next, 'cookies_page')
    // Only fire if turning ON — the off-path disables GA before this point, so the event would no-op.
    if (next) trackEvent('cookie_prefs_toggled', {analytics: true})
    flashSaved()
  }

  return (
    <div className="ck-prefs">
      <div className="ck-prefs-row locked">
        <div>
          <div className="ck-prefs-title">
            {t('cookies.prefs.strictlyNecessary.title')}
            <span className="ck-prefs-badge">{t('cookies.prefs.strictlyNecessary.alwaysOn')}</span>
          </div>
          <p className="ck-prefs-body">{t('cookies.prefs.strictlyNecessary.body')}</p>
        </div>
        <label className="ck-toggle" aria-disabled="true">
          <input type="checkbox" checked readOnly disabled />
          <span className="ck-toggle-track" />
        </label>
      </div>

      <div className="ck-prefs-row">
        <div>
          <div className="ck-prefs-title">
            {t('cookies.prefs.analytics.title')}
            <span className="ck-prefs-badge optional">{t('cookies.prefs.analytics.optional')}</span>
          </div>
          <p className="ck-prefs-body">{t('cookies.prefs.analytics.body')}</p>
        </div>
        <label className="ck-toggle">
          <input
            type="checkbox"
            checked={analyticsOn}
            onChange={(e) => handleToggleAnalytics(e.target.checked)}
            aria-label={t('cookies.prefs.analytics.title')}
          />
          <span className="ck-toggle-track" />
        </label>
      </div>

      <div className="ck-prefs-actions">
        <button type="button" className="ck-btn primary" onClick={() => handleToggleAnalytics(true)}>
          {t('cookies.prefs.actions.acceptAll')}
        </button>
        <button type="button" className="ck-btn ghost" onClick={() => handleToggleAnalytics(false)}>
          {t('cookies.prefs.actions.rejectOptional')}
        </button>
        <span className={`ck-saved ${saved ? 'show' : ''}`} aria-live="polite">
          {t('cookies.prefs.actions.saved')}
        </span>
      </div>
    </div>
  )
}

const CookiesPage = () => {
  const {t} = useTranslation()

  return (
    <div className="cookies-root">
      <div className="ck-grid" aria-hidden />

      <main className="cookies-page">
        <header className="ck-hero">
          <span className="ck-eyebrow">
            <span className="ck-eyebrow-dot" />
            {t('cookies.eyebrow')}
          </span>
          <h1 className="ck-title">
            {t('cookies.titleLead')} <em>{t('cookies.titleEm')}</em>
          </h1>
          <p className="ck-lede">{t('cookies.lede')}</p>
          <div className="ck-meta">
            <span>{t('cookies.metaUpdated')}</span>
            <span className="ck-meta-sep" />
            <span>{t('cookies.metaReading')}</span>
            <span className="ck-meta-sep" />
            <span>{t('cookies.metaGoverned')}</span>
          </div>
        </header>

        <div className="ck-layout">
          <aside className="ck-toc" aria-label={t('cookies.tocLabel')}>
            <div className="ck-toc-label">{t('cookies.tocLabel')}</div>
            {TOC.map((entry, i) => (
              <a key={entry.id} href={`#${entry.id}`}>
                <span className="ck-toc-num">{pad2(i + 1)}</span>
                <span>{t(`cookies.toc.${entry.key}`)}</span>
              </a>
            ))}
          </aside>

          <article className="ck-prose">
            <section id="prefs">
              <h2 data-num="01">{t('cookies.sections.prefs.title')}</h2>
              <p>{t('cookies.sections.prefs.p1')}</p>
              <CookiePrefsPanel />
            </section>

            <section id="intro">
              <h2 data-num="02">{t('cookies.sections.intro.title')}</h2>
              <p>{t('cookies.sections.intro.p1')}</p>
              <p>
                <Trans i18nKey="cookies.sections.intro.p2" components={PROSE_COMPONENTS} />
              </p>
            </section>

            <section id="what">
              <h2 data-num="03">{t('cookies.sections.what.title')}</h2>

              <h3>{t('cookies.sections.what.auth.title')}</h3>
              <ul>
                <li>
                  <Trans i18nKey="cookies.sections.what.auth.bullet" components={PROSE_COMPONENTS} />
                </li>
              </ul>

              <h3>{t('cookies.sections.what.recaptcha.title')}</h3>
              <ul>
                <li>
                  <Trans i18nKey="cookies.sections.what.recaptcha.bullet" components={PROSE_COMPONENTS} />
                </li>
              </ul>

              <h3>{t('cookies.sections.what.billing.title')}</h3>
              <ul>
                <li>
                  <Trans i18nKey="cookies.sections.what.billing.bullet" components={PROSE_COMPONENTS} />
                </li>
              </ul>

              <h3>{t('cookies.sections.what.optional.title')}</h3>
              <ul>
                <li>
                  <Trans i18nKey="cookies.sections.what.optional.ga" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="cookies.sections.what.optional.sentry" components={PROSE_COMPONENTS} />
                </li>
              </ul>

              <h3>{t('cookies.sections.what.storage.title')}</h3>
              <ul>
                <li>
                  <Trans i18nKey="cookies.sections.what.storage.email" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="cookies.sections.what.storage.consent" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="cookies.sections.what.storage.firebaseDb" components={PROSE_COMPONENTS} />
                </li>
              </ul>
            </section>

            <section id="not">
              <h2 data-num="04">{t('cookies.sections.not.title')}</h2>
              <ul>
                <li>{t('cookies.sections.not.bullets.ads')}</li>
                <li>{t('cookies.sections.not.bullets.replay')}</li>
                <li>{t('cookies.sections.not.bullets.abTest')}</li>
                <li>{t('cookies.sections.not.bullets.crossSite')}</li>
              </ul>
              <p>{t('cookies.sections.not.p1')}</p>
            </section>

            <section id="control">
              <h2 data-num="05">{t('cookies.sections.control.title')}</h2>
              <p>{t('cookies.sections.control.p1')}</p>
              <ul>
                <li>
                  <Trans i18nKey="cookies.sections.control.bullets.panel" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="cookies.sections.control.bullets.browser" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="cookies.sections.control.bullets.gaOptOut" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="cookies.sections.control.bullets.dnt" components={PROSE_COMPONENTS} />
                </li>
              </ul>
            </section>

            <section id="changes">
              <h2 data-num="06">{t('cookies.sections.changes.title')}</h2>
              <p>{t('cookies.sections.changes.p1')}</p>
              <div className="ck-contact-cta">
                <ContactUsButton />
              </div>
            </section>
          </article>
        </div>
      </main>
    </div>
  )
}

export default CookiesPage
